import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const getViewportMetrics = () => {
  const visualViewport = window.visualViewport;
  const width = Math.round(visualViewport?.width || window.innerWidth || document.documentElement.clientWidth || 0);
  const height = Math.round(visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0);
  const layoutHeight = Math.round(window.innerHeight || document.documentElement.clientHeight || height || 0);

  return {
    width,
    height,
    layoutHeight,
    offsetLeft: Math.round(visualViewport?.offsetLeft || 0),
    offsetTop: Math.round(visualViewport?.offsetTop || 0)
  };
};

const CustomSelect = ({ options, value, onChange, placeholder = 'Selecione...', searchPlaceholder = 'Buscar...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 300, mobile: false });
  const dropdownRef = useRef(null);
  const searchContainerRef = useRef(null);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(
      options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar se o clique foi dentro do wrapper OU dentro do dropdown portal
      const isInsideWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);

      if (!isInsideWrapper && !isInsideDropdown) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Manter o foco no input de pesquisa quando o dropdown está aberto
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, filteredOptions]);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const updatePosition = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          const viewport = getViewportMetrics();
          const isMobileViewport = viewport.width <= 768;
          const horizontalMargin = isMobileViewport ? 12 : 8;
          const verticalMargin = 12;

          const keyboardOpen = isMobileViewport && viewport.layoutHeight > 0 && viewport.height < (viewport.layoutHeight * 0.8);

          if (keyboardOpen) {
            setDropdownPosition({
              top: viewport.offsetTop + verticalMargin,
              left: viewport.offsetLeft + horizontalMargin,
              width: Math.max(220, viewport.width - (horizontalMargin * 2)),
              maxHeight: Math.max(180, Math.min(260, viewport.height - (verticalMargin * 2))),
              mobile: true
            });
            return;
          }

          const maxWidth = Math.max(220, viewport.width - (horizontalMargin * 2));
          const width = Math.min(Math.round(rect.width), maxWidth);
          const minLeft = viewport.offsetLeft + horizontalMargin;
          const maxLeft = viewport.offsetLeft + viewport.width - width - horizontalMargin;
          const left = Math.min(Math.max(Math.round(rect.left), minLeft), Math.max(minLeft, maxLeft));
          const spaceBelow = viewport.offsetTop + viewport.height - rect.bottom - verticalMargin;
          const spaceAbove = rect.top - viewport.offsetTop - verticalMargin;
          const openAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
          const maxHeight = Math.max(160, Math.min(openAbove ? spaceAbove : spaceBelow, isMobileViewport ? 240 : 300));
          const top = openAbove
            ? Math.max(viewport.offsetTop + verticalMargin, Math.round(rect.top - maxHeight))
            : Math.round(rect.bottom);

          setDropdownPosition({
            top,
            left,
            width,
            maxHeight,
            mobile: false
          });
        }
      };

      updatePosition();

      let ticking = false;
      const handleScrollOrResize = (e) => {
        // Ignorar eventos dentro do dropdown ou do campo de busca
        if (e.target) {
          const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
          const isInsideSearch = searchContainerRef.current && searchContainerRef.current.contains(e.target);

          if (isInsideDropdown || isInsideSearch) {
            return;
          }
        }

        if (!ticking) {
          window.requestAnimationFrame(() => {
            updatePosition();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      window.visualViewport?.addEventListener('resize', handleScrollOrResize);
      window.visualViewport?.addEventListener('scroll', handleScrollOrResize);

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
        window.visualViewport?.removeEventListener('resize', handleScrollOrResize);
        window.visualViewport?.removeEventListener('scroll', handleScrollOrResize);
      };
    }
  }, [isOpen]);

  // Comparação flexível para lidar com string vs number
  const selectedOption = options.find(opt => String(opt.value) === String(value));

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setSearchTerm('');
  };

  const handleSelect = (optionValue) => {
    console.log('🔄 CustomSelect - handleSelect chamado');
    console.log('   Valor selecionado:', optionValue, '(tipo:', typeof optionValue + ')');
    console.log('   Value atual:', value, '(tipo:', typeof value + ')');
    console.log('   onChange:', !!onChange);

    if (onChange && typeof onChange === 'function') {
      onChange(optionValue);
      console.log('   ✅ onChange executado com valor:', optionValue);
    } else {
      console.error('   ❌ onChange não é uma função válida!');
    }

    setIsOpen(false);
    setSearchTerm('');
  };

  const DropdownPortal = () => {
    if (!isOpen) return null;

    const isMobileViewport = dropdownPosition.mobile || getViewportMetrics().width <= 768;

    const dropdownStyle = {
      position: 'fixed',
      top: `${dropdownPosition.top}px`,
      left: `${dropdownPosition.left}px`,
      width: `${dropdownPosition.width}px`,
      maxWidth: isMobileViewport ? 'none' : `calc(100vw - ${isMobileViewport ? 24 : 16}px)`,
      maxHeight: `${dropdownPosition.maxHeight || 300}px`,
      zIndex: 9999999,
      WebkitTransform: 'translateZ(0)',
      transform: 'translateZ(0)',
      willChange: 'transform',
      WebkitOverflowScrolling: 'touch'
    };

    return createPortal(
      <div
        ref={dropdownRef}
        className="custom-select-dropdown active"
        style={dropdownStyle}
      >
        <div
          ref={searchContainerRef}
          className="custom-select-search"
        >
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchPlaceholder}
            autoComplete="off"
            autoFocus={true}
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        <div className="custom-select-options">
          {filteredOptions.map((option, index) => (
            <div
              key={`${option.value}-${index}`}
              className={`custom-select-option ${String(option.value) === String(value) ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
          {filteredOptions.length === 0 && (
            <div className="custom-select-empty">
              Nenhuma opção encontrada
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={`custom-select-wrapper ${isOpen ? 'active' : ''}`}
        style={{ 
          position: 'relative', 
          width: '100%'
        }}
      >
        <div
          ref={triggerRef}
          className={`custom-select-trigger ${isOpen ? 'active' : ''} ${selectedOption ? '' : 'is-placeholder'}`}
          onClick={handleToggle}
          style={{ zIndex: isOpen ? 10 : 1 }}
        >
          <span>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <i
            className={`fas fa-chevron-down ${isOpen ? 'open' : ''}`}
          ></i>
        </div>
      </div>

      <DropdownPortal />
    </>
  );
};

export default CustomSelect;
